import { handleErrorWithSentry } from '@sentry/sveltekit';
import * as Sentry from '@sentry/sveltekit';
import type { ClientInit } from '@sveltejs/kit/hooks';
import { sentryDataCollection } from '#lib/constants/sentryDataCollection.js';
import { hasFrozenBuiltIns } from '#lib/utils/hasFrozenBuiltIns/hasFrozenBuiltIns.js';
import { isInjectedScriptError } from '#lib/utils/isInjectedScriptError/isInjectedScriptError.js';
import { polyfillBrowserApis } from '#lib/utils/polyfillBrowserApis/polyfillBrowserApis.js';
import { reloadOnStaleAssets } from '#lib/utils/reloadOnStaleAssets/reloadOnStaleAssets.js';
import { dev } from '$app/env';
import { PUBLIC_APP_ENV } from '$app/env/public';
import { initAnalytics } from '$global/utils/track';

Sentry.init({
	// Compared inline, not via isOnProductionOnly(): the bundler only folds the
	// dev DSN out of the production build when it sees the literal comparison.
	dsn:
		PUBLIC_APP_ENV === 'production'
			? 'https://534a6c72f87391db9b8aaa292250da34@e.heni.com/1'
			: 'https://917dfca3d6114d15397b29bb9ce439c2@damaged-values.no-zero.net/8',
	// Sentry is used for issues only: no tracing (compiled out, see
	// vite.config.ts) and no release-health sessions, which sent a request on
	// every page view.
	integrations: (defaults) =>
		defaults.filter((integration) => integration.name !== 'BrowserSession'),
	// Set here once rather than injected into every chunk (see vite.config.ts).
	release: __HENI_SENTRY_RELEASE__ || undefined,
	// Keeps Sentry 10's restrictive defaults (see sentryDataCollection).
	dataCollection: sentryDataCollection,
	ignoreErrors: [
		/Java object is gone/i,
		/window.webkit.messageHandlers/i,
		// Ignore transient network errors during client-side navigation
		/Failed to fetch/i,
		/Load failed/i,
		/NetworkError/i,
		/Network request failed/i,
		// Chromium's wording for a failed request during client-side navigation
		/^network error$/i,
		// Ignore chunk loading errors during deployments (auto-reload handles these)
		/Failed to fetch dynamically imported module/i,
		/Importing a module script failed/i,
		/error loading dynamically imported module/i,
		// Ignore CSS preload errors during deployments (auto-reload handles these)
		/Unable to preload CSS/i,
		// Ignore localStorage SecurityErrors (Safari private mode, cross-origin iframes)
		/The operation is insecure/i,
		// Ignore third-party script errors (Google Ads, Reddit, etc.)
		/Object captured as promise rejection/i,
		// Ignore browser extension/external script interference
		/__firefox__/i,
		/runtime\.sendMessage/i,
		/DarkReader/,
		/window\.ethereum/,
		// Outlook's Safe Links scanner rejects with this when it renders our pages
		/Object Not Found Matching Id/,
		// In-app browsers' native bridges (DuckDuckGo, Chrome iOS, Zalo)
		/WKWebView/,
		/zaloJSV2/,
		// iOS app web views whose native message handler has gone away
		/Script message handler is unavailable/,
		// Not in our code: thrown by a script an iOS app web view injects
		/Stylesheet append timeout/,
		// A tab still running the previous deployment imports a chunk that no
		// longer has that export (auto-reload handles these)
		/does not provide an export named/i,
		// Sentry's own web-vitals instrumentation in some iOS web views
		/WeakMap keys must be objects/,
		// We don't use jQuery: scripts injected into the page that expect it
		/jQuery is not defined/,
	],
	denyUrls: [
		// Ignore errors from third-party scripts
		/googletagmanager\.com/i,
		/google-analytics\.com/i,
		/googleadservices\.com/i,
		/redditstatic\.com/i,
		/clarity\.ms/i,
		/facebook\.net/i,
		/doubleclick\.net/i,
		/hscollectedforms\.net/i,
		/hs-scripts\.com/i,
		/hs-analytics\.net/i,
		/licdn\.com/i,
		/exponea\.com/i,
		/getsocialbot\.com/i,
		/translate\.googleapis\.com/i,
		// Startpage's anonymous view proxies (and rewrites) every script
		/startpage\.com\/__av\//i,
		/extensions\//i,
		/^chrome:\/\//i,
		/^chrome-extension:\/\//i,
		/^moz-extension:\/\//i,
		// Safari masks extension script URLs as webkit-masked-url://hidden/
		/^webkit-masked-url:\/\//i,
		// Scripts injected by the Facebook/Instagram in-app browser
		/^iabjs:\/\//i,
	],
	beforeSend(event) {
		// Ignore errors from crawlers: they run third-party scripts in stripped-down
		// browsers and report errors no visitor can hit.
		const userAgent = event.request?.headers?.['User-Agent'] || '';

		if (
			/baiduspider|googlebot|bingbot|slurp|duckduckbot|bytespider|tiktokspider|meta-webindexer|meta-externalagent|facebookexternalhit|lightpanda/i.test(
				userAgent
			)
		) {
			return null;
		}

		// Instagram/Facebook form logging and Google Translate on iOS, injected
		// under the page's own URL
		if (
			isInjectedScriptError(event, navigator.userAgent, window.location.href)
		) {
			return null;
		}

		// Someone running the site locally against the production DSN
		if (
			PUBLIC_APP_ENV === 'production' &&
			/^(localhost|127\.0\.0\.1|\[::1\])$/.test(window.location.hostname)
		) {
			return null;
		}

		// Built-in prototypes frozen by a hardened headless browser
		if (hasFrozenBuiltIns()) {
			return null;
		}

		// The page served through Google Translate's proxy (heni-com.translate.goog):
		// on that origin our scripts hit CORS and other errors heni.com never sees
		if (window.location.hostname.endsWith('.translate.goog')) {
			return null;
		}

		return event;
	},
});

// Custom error handler that detects chunk loading failures and reloads the page
export const handleError = handleErrorWithSentry(
	(input: { error: unknown }) => {
		const { error } = input;

		reloadOnStaleAssets(error);

		// Note: Sentry filtering is handled by ignoreErrors in Sentry.init() above
		// handleErrorWithSentry() automatically reports errors that aren't filtered

		// Returning nothing renders every client-nav failure as a bare
		// "Internal Error" — in dev, surface the real message so failures
		// are diagnosable. Production keeps the generic message.
		if (dev && error instanceof Error) {
			return { message: error.message };
		}
	}
);

// Analytics are opt-in per app (see initAnalytics). `init` runs before
// hydration, so components that track on mount are not dropped.
export const init: ClientInit = async () => {
	await polyfillBrowserApis();
	initAnalytics();
};
